import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { FormField, FormItem } from '@/components/ui/form';
import { format } from 'date-fns';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { Label } from './ui/label';

interface FormFieldCalendarProps<T extends FieldValues> {
  label: string;
  name: Path<T>;
  disabledAt?: (date: Date) => boolean;
  disablePastDates?: boolean;
  placeholder?: string;
  required?: boolean;
  control?: Control<T>;
  iconColor?: string;
  className?: string;
  fieldClassName?: string;
  hasControl?: boolean;
  defaultValue?: string | Date;
  onChange?: (value: Date | undefined) => void;
  disabled?: boolean;
  disabledStyle?: string;
  tooltip?: string;
  clearable?: boolean;
}

const FormFieldCalendar = <T extends FieldValues>({
  control,
  placeholder,
  name,
  label,
  required,
  iconColor = '#969696',
  className,
  disabledAt,
  disablePastDates,
  hasControl = true,
  fieldClassName,
  defaultValue,
  onChange,
  disabled,
  disabledStyle,
  tooltip,
  clearable = false,
}: FormFieldCalendarProps<T>) => {
  const { field } = useController({ name, control });

  const handleDisabledAt = (date: Date) => {
    if (disabledAt) return disabledAt(date);
    if (disablePastDates) {
      return date < new Date(new Date().setHours(0, 0, 0, 0));
    }
    return false;
  };
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (field.value && hasControl) field.onChange(field.value);
  }, []);

  const calendar = () => {
    const value = hasControl ? field.value : defaultValue ? new Date(defaultValue) : undefined;

    const handleClear = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (hasControl) field.onChange(undefined);
      if (onChange) onChange(undefined);
    };

    return (
      <Popover
        modal
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open && hasControl) field.onBlur();
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant={'outline'}
            onClick={() => setIsOpen(true)}
            disabled={disabled}
            className={`flex h-12 w-full rounded-md bg-transparent text-[#0A0A0A] px-3 py-1 text-sm font-[400] border-[1px] border-bordercolor !placeholder:text-placeholder placeholder:font-normal file:text-sm file:font-medium focus:border-[1px] focus:border-focuscolor disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none hover:bg-white bg-white ${fieldClassName ?? ''} ${disabledStyle ?? ''}`}
          >
            {value ? format(value, 'MM/dd/yyyy') : <span className="text-placeholder ">{placeholder}</span>}
            <div className="ml-auto flex flex-row gap-2 items-center">
              {clearable && value && (
                <X className="h-4 w-4 text-[#969696] hover:text-[#666666]" onClick={handleClear} />
              )}
              <CalendarIcon className="h-4 w-4" style={{ color: iconColor }} />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 " align="end">
          <Calendar
            mode="single"
            selected={value}
            onSelect={(e) => {
              if (hasControl) field.onChange(e);
              if (onChange) onChange(e);
              field.onBlur();
              setIsOpen(false);
            }}
            disabled={handleDisabledAt}
            initialFocus
            className={className}
          />
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <FormItem className={`flex flex-col gap-1 `}>
      <Label htmlFor={name} className="block text-sm font-medium flex items-center gap-1">
        {label}
        {required && <span className="text-error align-top">*</span>}
        {tooltip && (
          <div className="group relative flex items-center">
            <span className="cursor-pointer text-muted-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-info"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
            </span>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden w-max max-w-xs rounded bg-popover px-2 py-1 text-sm text-popover-foreground shadow-md group-hover:block z-50 border">
              {tooltip}
            </div>
          </div>
        )}
      </Label>
      {hasControl ? (
        <FormField
          control={control}
          name={name}
          render={({ fieldState }) => (
            <FormItem className="flex flex-col">
              {calendar()}
              {fieldState.error && <p className="text-error text-xs">{fieldState.error.message}</p>}
            </FormItem>
          )}
        />
      ) : (
        calendar()
      )}
    </FormItem>
  );
};

export default FormFieldCalendar;
